// Mapbox public access token (maps + geocoding). Comes from the env so the
// token isn't committed — set REACT_APP_MAPBOX_TOKEN in .env locally, and as a
// Docker build-arg in CI (see Dockerfile).
export const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN ?? "";

// ecoFutures Studio style (since 2026-10-11). A classic Satellite Streets
// style with globe projection baked in — it doesn't import Mapbox Standard.
// The previous Studio style (cmqor80wg00c701s5heusczen) was Standard-based,
// which needs mapbox-gl v3+ (on v2 it loads without errors and renders blank).
export const MAPBOX_STYLE = "mapbox://styles/sureshmichael/cmv3vfgc300kj01s0aku7dvx0";

// Homepage hero's static map style (since 2026-10-13), rendered via the Static Images API
// (utils/staticMap). A copy of the classic Satellite Streets style, cut down to imagery plus
// natural-feature labels (peaks, water): no roads or place names. Same sources as MAPBOX_STYLE.
export const HERO_MAPBOX_STYLE = "mapbox://styles/sureshmichael/cmv6bkv09001d01qv0x7ocfkh";

// `any`: react-map-gl 7's MapLib type was written against mapbox-gl v2 and rejects v3's typings.
let mapboxgl: Promise<any> | null = null;

/**
 * mapbox-gl, set up for this build, for react-map-gl's `mapLib` prop. Every <Map> passes it
 * (LandPage's background map has its own copy), so nothing imports mapbox-gl statically. It's
 * ~500KB gzip, and as a static import (it used to live in src/index.tsx) it sat in the main bundle,
 * on the critical path of every page's first paint, `/` included, which has no Mapbox GL map. As
 * an async chunk it downloads alongside the app (react-map-gl requests it when it loads), and
 * each map awaits the setup below before it's created.
 */
export const loadMapboxgl = () => {
  if (!mapboxgl) {
    mapboxgl = import("mapbox-gl").then(({ default: lib }) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line import/no-webpack-loader-syntax, import/no-unresolved
      lib.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

      // mapbox-gl v3 changed supported() to take a bare `failIfMajorPerformanceCaveat`
      // boolean, but react-map-gl 7.x still calls supported(props) with the whole
      // props object — always truthy, so the strict check runs and software WebGL
      // (VMs, GPU acceleration off, headless) throws "Map is not supported by this
      // browser". Forward only the boolean. react-map-gl 8 drops this gate entirely.
      const mapboxSupported = lib.supported.bind(lib) as (strict?: boolean) => boolean;
      (lib as any).supported = (options?: unknown) =>
        mapboxSupported(
          typeof options === "boolean"
            ? options
            : Boolean((options as { failIfMajorPerformanceCaveat?: boolean } | undefined)?.failIfMajorPerformanceCaveat)
        );
      return lib;
    });
  }
  return mapboxgl;
};
